import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { api_getProductStoreAvailability } from '~/backend/api_calls';
import ShopCart from '~/backend/shop_cart';
import UserState from '~/backend/user_session';
import { sendAddedToCartKlaviyoEvent } from '~/common/klaviyo.js';
import { notifyError, notifyInfo } from '~/components/common/ToastMessages';

import { addToCartAction, addToWishlistAction, openCartWidgetAction } from '~/store/actions.js';
import SizeGuide from './SizeGuide';

import { sendAddToCartFacebookEvent } from '~/common/facebookPixel.js';
import { sendAddToCartGTAG4Event } from '~/common/gtag4.js';
import { sendAddToCartGTMEvent } from '~/common/gtm.js';
import { sendAddToCartTiktokEvent } from '~/common/tiktokPixel.js';

import { useLocation } from 'react-router';
import GuestWishlist from '~/backend/guest_wishlist';
import UserSession from '~/backend/user_session';
import { calculateProductPriceValue } from '~/common/helper.js';
import { simplerProduct } from '~/common/simpler.js';
import AvailabilityInStores from '~/components/product/AvailabilityInStores';
import { toggleStoreAvailabilityModal } from '~/store/availability/actions';

const AddToCart = ({ product, setProductPictures, defaultProductPictures, setChosenColorParent }) => {
	const { t } = useTranslation();
	const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

	const [sizes, setSizes] = useState([]);
	const [chosenSize, setChosenSize] = useState(0);
	const [chosenColor, setChosenColor] = useState(0);
	const [storeAvailabilityData, setStoreAvailabilityData] = useState(null);

	const dispatch = useDispatch();
	const dimensions = product.dimensions;
	const location = useLocation();
	const isAvailabilityModalOpen = useSelector(state => state.availabilityReducer.storeAvailabilityModalOpen);

	const userAuthenticated = UserSession.isAuthenticated();
	const [wishlistActive, setWishlistActive] = useState(product.inwishlist);
	const uniqueColors = [...new Map(dimensions.filter(item => item.cval).map(item => [item['cval'], item])).values()];
	const sortedByDscUniqueColors = uniqueColors.slice().sort((a, b) => {
		if (a.dsc === undefined) return 1; // Move undefined values to the end
		if (b.dsc === undefined) return -1; // Move undefined values to the end
		return b.dsc - a.dsc;
	});

	useEffect(() => {
		if (uniqueColors.length > 0) {
			const firstColor = sortedByDscUniqueColors[0];
			const dimensionsIds = dimensions.filter(x => x.cval == firstColor.cval).map(y => y.id);
			const sizes = dimensions.filter(x => x.cval == null && dimensionsIds.includes(x.id));
			setChosenColor(firstColor.cvtxt);
			setChosenColorParent(firstColor.cval);
			setSizes(sizes);
			setColorPictures(sizes, firstColor);
		} else {
			setSizes(dimensions);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dimensions]);

	useEffect(() => {
		// Reset size selection if user navigates to another product.
		handleChooseSize(0);
	}, [location]);

	useEffect(() => {
		simplerProduct(product.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const handleChooseSize = size => {
		setChosenSize(size);
	};

	const handleColorChange = color => {
		const dimensionsIds = dimensions.filter(x => x.cval == color).map(y => y.id);
		const sizes = dimensions.filter(x => x.cval == null && dimensionsIds.includes(x.id));

		{
			/* Reset thumb and image for different color */
		}
		let imageviewer = window.document.getElementById('image-viewer');
		let firstthumb = window.document.querySelector('.thumb-item.img-1');
		if (typeof imageviewer != 'undefined' && imageviewer != null) {
			imageviewer.scrollTo({
				top: 0,
				behavior: 'instant'
			});
		}
		if (typeof firstthumb != 'undefined' && firstthumb != null) {
			firstthumb.click();
		}

		setSizes(sizes);
		setChosenColor(dimensions.find(x => x.cval == color).cvtxt);
		setChosenColorParent(color);
		handleChooseSize(0);
		setColorPictures(sizes, color);
	};

	const handleAddToCart = () => {
		if (sizes.length > 0 && chosenSize == 0) {
			notifyError(t('select_size'));
			return;
		}
		const price = calculateProductPriceValue(product, chosenSize);
		if (!price || price <= 0) {
			notifyError(t('zero_price'));
			return;
		}

		dispatch(
			addToCartAction({
				pid: product.id,
				dimension: chosenSize,
				quantity: 1,
				product: product
			})
		);

		sendAddedToCartKlaviyoEvent(UserState.getEmail(), product, ShopCart.getItems());

		if (window.innerWidth > 900) {
			dispatch(openCartWidgetAction(true));
			document.body.classList.add('cart-open');

			setTimeout(() => {
				document.body.classList.remove('cart-open');
				dispatch(openCartWidgetAction(false));
			}, 2000);
		}

		notifyInfo(t('added_to_cart'));
		sendAddToCartGTAG4Event(product, chosenSize, 1);
		sendAddToCartFacebookEvent(product);
		sendAddToCartTiktokEvent(product);
		sendAddToCartGTMEvent(product, chosenSize, 1);
	};

	const setColorPictures = (sizes, color) => {
		//if (color === [...uniqueColors][0]) {
		//    defaultProductPictures(product);
		//    return;
		//}

		let imgArray = [];
		for (let i = 0; i < sizes.length; i++) {
			let size = sizes[i];
			if (size.img1) imgArray.push(size.img1);

			if (size.img2) imgArray.push(size.img2);

			if (size.img3) imgArray.push(size.img3);

			if (size.img4) imgArray.push(size.img4);

			if (imgArray.length > 0) break;
		}

		if (imgArray.length > 0) {
			setProductPictures([...new Set(imgArray)]);
		}
	};
	const showColors = dimensions => {
		if (dimensions != null && dimensions.length > 1) {
			return sortedByDscUniqueColors.map((item, index) => (
				<React.Fragment key={item.cval}>
					<input
						type="radio"
						id={'product_' + product.id + '_color_' + index}
						name={'product_' + product.id + '_color'}
						value={'product_' + product.id + '_color_' + index}
						onClick={() => handleColorChange(item.cval)}
						defaultChecked={index === 0}
					/>
					<label
						htmlFor={'product_' + product.id + '_color_' + index}
						style={{
							backgroundColor: item.chex
						}}>
						{' '}
					</label>
				</React.Fragment>
			));
		}
		return null;
	};

	const showSizes = sizes => {
		if (sizes != null && sizes.length > 0) {
			sizes.sort((a, b) => a.si - b.si);
			return sizes.map((item, index) => (
				<button
					onClick={() => handleChooseSize(item.id)}
					className={item.id == chosenSize ? 'size-box selected-size' : 'size-box'}
					disabled={!item.qty || item.qty <= 0}
					key={product.id + '-' + index}>
					{item.svtxt}
				</button>
			));
		}
		return null;
	};

	const handleWishlistTrigger = () => {
		if (userAuthenticated) {
			dispatch(
				addToWishlistAction({
					productId: product.id
				})
			);
		} else {
			let status = GuestWishlist.addOrRemove(product);
			let message = '';

			if (status === 'added') {
				message = t('product_added_to_wishlist_guest');
				setWishlistActive(true);
			} else {
				message = t('product_removed_from_wishlist_guest');
				setWishlistActive(false);
			}

			notifyInfo(message);
		}
	};
	if (Object.keys(product).length === 0) return null;

	const toggleAvailabilityModal = async () => {
		if (!chosenColor || !chosenSize) {
			if (uniqueColors.length === 1) {
				const defaultColor = uniqueColors[0];
				const colorValue = defaultColor.cval;
				const dimensionsIds = dimensions.filter(x => x.cval == colorValue).map(y => y.id);
				const sizes = dimensions.filter(x => x.cval == null && dimensionsIds.includes(x.id));

				setChosenColor(defaultColor.cvtxt);
				setChosenColorParent(colorValue);
				setSizes(sizes);
				handleChooseSize(sizes[0]?.id || 0);
				setColorPictures(sizes, defaultColor);
			} else {
				notifyError(t('select_color_and_size_first'));
				return;
			}
		}

		const selectedSizeDimension = product?.dimensions.find(dim => dim.id === chosenSize && dim.cvtxt === chosenColor);
		const barcode = selectedSizeDimension?.bcd;

		try {
			const availability = await api_getProductStoreAvailability(barcode);
			setStoreAvailabilityData(availability);

			document.body.classList.add('toggle-store-availability-modal');
			dispatch(toggleStoreAvailabilityModal(true));
		} catch (error) {
			console.error('Availability fetch error:', error);
			notifyError(t('availability_fetch_failed'));
		}
	};
	return (
		<>
			{isAvailabilityModalOpen && storeAvailabilityData?.Rows?.length >= 0 && (
				<AvailabilityInStores product={product} availability={storeAvailabilityData} />
			)}

			{chosenColor !== 0 && chosenColor !== undefined ? (
				<div className="color-container">
					{t('product_color')} <span>{chosenColor}</span>
					<div className="prod-colors flex-col">{showColors(dimensions)}</div>
				</div>
			) : null}

			{sizes.length > 0 ? <div className="sizes">{showSizes(sizes)}</div> : null}
			<button id="store-availability" className="availability-modal-button" type="button" onClick={toggleAvailabilityModal}>
				{t('availability_in_stores')}
			</button>

			{/*<div className="recommended-size">
                {t("product_recommended_size")} <span>40</span>
            </div>*/}

			<div className="buy-section">
				<div className="add-to-cart" onClick={handleAddToCart}>
					{t('add_to_cart')}
				</div>
				<div className={wishlistActive ? 'add-wishlist active' : 'add-wishlist'} onClick={() => handleWishlistTrigger()}>
					<svg xmlns="http://www.w3.org/2000/svg" width="19.141" height="16.77" viewBox="0 0 19.141 16.77">
						<path
							id="Icon_feather-heart"
							data-name="Icon feather-heart"
							d="M18.788,5.865a4.714,4.714,0,0,0-6.64,0l-.9.9-.9-.9a4.715,4.715,0,0,0-6.64,0,4.649,4.649,0,0,0,0,6.6l.9.9,6.64,6.6,6.64-6.6.9-.9a4.648,4.648,0,0,0,0-6.6Z"
							transform="translate(-1.673 -3.847)"
							fill="none"
							stroke="#000"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="1.3"
						/>
					</svg>
				</div>
				<div className="simpler-btn" id="simpler-product-page"></div>
				<div className="missing-size">
					{product.sgtl && product.sgbd && (
						<button onClick={() => setSizeGuideOpen(true)} style={{ textDecoration: 'underline' }}>
							Οδηγός μεγεθών
						</button>
					)}
					<br />
					{t('product_missing_recommended_size')} <a href="tel: 00302103400100">{t('call_us')}</a>
				</div>
			</div>
			{product.sgtl && product.sgbd && (
				<SizeGuide title={product.sgtl} body={product.sgbd} open={sizeGuideOpen} setOpen={setSizeGuideOpen} />
			)}
		</>
	);
};

AddToCart.propTypes = {
	product: PropTypes.object,
	setProductPictures: PropTypes.any,
	defaultProductPictures: PropTypes.any,
	setChosenColorParent: PropTypes.any
};

export default AddToCart;
